import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Box, Grid, List, ListItem, ListItemText, Tabs, Tab, Typography } from '@material-ui/core';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import {
  RMSTextField,
  RMSSelect,
  RMSCheckbox,
  RMSRadioGroup,
} from '../../../shared/components/form-controls';
import { RMSButton } from '../../../shared/components/buttons';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import CreateFormResolver from '../../../shared/utility/createFormResolver';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import useDebouncedValue from '../../../shared/utility/useDebouncedValue';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import addAddressSchema from '../../schema/addAddressSchema';
import {
  DEFAULT_VALUES,
  DICT_SELECT_PROPS,
  ESN_SELECT_PROPS,
  CLERY_LOCATION_OPTIONS,
  ADDRESS_TABS,
  ADDRESS_TAB_KEYS,
} from '../../constants/addAddressFormConstants';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import SectionLabel from './SectionLabel';
import AddIcon from '@material-ui/icons/Add';
import CityManagementDialog from './CityManagementDialog';
import AddressAttachmentsTab from './AddressAttachmentsTab';
import AddressCoordinatesTab from './AddressCoordinatesTab';
import AddressHistoryTab from './AddressHistoryTab';
import AddressPartiesTab from './AddressPartiesTab';
import AddressPrePlanTab from './AddressPrePlanTab';
import AddressSopsTab from './AddressSopsTab';
import AddressZonesTab from './AddressZonesTab';
import {
  parsePostcode,
  buildFullAddressText,
  mapDuplicateCheckFields,
  mapCleryLocationFlags,
  buildPostal,
  mapCleryFlagsToRadio,
} from '../../utility/addAddressHelper';
import {
  createAddress,
  getAddressDuplicateCheckFields,
  findAddressDuplicates,
  getAddressDetails,
  getAddressSops,
} from '../../services/userService';

const AddAddressDialog = ({ open, onClose, addressId = null, onAddressCreated }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.userData.user);
  const cityDictPerms = useSelector((state) => state.permissions?.dictionary?.codeCities);
  const isEditMode = Boolean(addressId);
  const addressPerms = getFullPermissions('globals', 'Addresses', 'any');
  const canSaveAddress = isEditMode ? addressPerms.Edit : true;
  const isViewOnly = isEditMode && !addressPerms.Edit;
  const [activeTab, setActiveTab] = useState(ADDRESS_TAB_KEYS.EDIT);
  const [sopsCount, setSopsCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [loadingDuplicateConfig, setLoadingDuplicateConfig] = useState(false);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [duplicateCheckFields, setDuplicateCheckFields] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [duplicateListOpen, setDuplicateListOpen] = useState(false);
  const [cityManagementDialogOpen, setCityManagementDialogOpen] = useState(false);
  const [coordinateState, setCoordinateState] = useState({ coordinate: null, submitting: false });
  const [prePlanState, setPrePlanState] = useState({ prePlan: null, submitting: false });
  const coordinatesTabRef = useRef(null);
  const prePlanTabRef = useRef(null);

  const methods = useForm({
    resolver: CreateFormResolver(addAddressSchema),
    mode: 'onSubmit',
    defaultValues: DEFAULT_VALUES,
    disabled: isViewOnly,
  });

  const watchedFields = useWatch({
    control: methods.control,
    name: [
      'AddressNumberPrefix',
      'AddressNumber',
      'AddressNumberSuffix',
      'PreDirection',
      'StreetName',
      'StreetType',
      'PostDirection',
      'UnitType',
      'UnitIdentifier',
      'ptsCityID',
      'State',
      'Postal',
    ],
  });

  const [
    AddressNumberPrefix,
    AddressNumber,
    AddressNumberSuffix,
    PreDirection,
    StreetName,
    StreetType,
    PostDirection,
    UnitType,
    UnitIdentifier,
    ptsCityID,
    State,
    Postal,
  ] = watchedFields;

  const [recordCreated, recordCreatedBy, recordUpdated, recordUpdatedBy] = useWatch({
    control: methods.control,
    name: ['Created', 'CreatedBy', 'Updated', 'UpdatedBy'],
  });

  const showCityAddIcon = Boolean(
    cityDictPerms?.Create || cityDictPerms?.Edit || cityDictPerms?.Delete
  );
  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const {
    streetDirectionOptions: predirectionalOptions,
    streetDirectionOptions: postdirectionalOptions,
    streetTypeOptions,
    cityOptions,
    stateOptions,
    unitTypeOptions,
    addressCategoryOptions,
    floorTypeOptions,
    bldgTypeOptions,
    esnOptions,
  } = useUserDictionaryOptions();

  const cityDisplayOptions = useMemo(
    () =>
      cityOptions.map((city) => ({
        ...city,
        DisplayLabel: city.State ? `${city.Description} - ${city.State}` : city.Description,
      })),
    [cityOptions]
  );

  const cityDescription = useMemo(
    () => cityOptions.find((city) => city.Code === ptsCityID)?.Description,
    [cityOptions, ptsCityID]
  );

  const fullAddressText = useMemo(
    () =>
      buildFullAddressText(
        {
          AddressNumberPrefix,
          AddressNumber,
          AddressNumberSuffix,
          PreDirection,
          StreetName,
          StreetType,
          PostDirection,
          UnitType,
          UnitIdentifier,
          State,
          Postal,
        },
        cityDescription
      ),
    [
      AddressNumberPrefix,
      AddressNumber,
      AddressNumberSuffix,
      PreDirection,
      StreetName,
      StreetType,
      PostDirection,
      UnitType,
      UnitIdentifier,
      State,
      Postal,
      cityDescription,
    ]
  );

  const candidateValues = useMemo(() => {
    const { PostalCode, PostalCodeExtension } = parsePostcode(Postal);
    return {
      AddressNumber: AddressNumber || null,
      AddressNumberPrefix: AddressNumberPrefix || null,
      AddressNumberSuffix: AddressNumberSuffix || null,
      PreDirection: PreDirection || null,
      StreetName: StreetName || null,
      StreetType: StreetType || null,
      PostDirection: PostDirection || null,
      UnitType: UnitType || null,
      UnitIdentifier: UnitIdentifier || null,
      ptsCityID: ptsCityID || null,
      State: State || null,
      PostalCode,
      PostalCodeExtension,
    };
  }, [
    AddressNumber,
    AddressNumberPrefix,
    AddressNumberSuffix,
    PreDirection,
    StreetName,
    StreetType,
    PostDirection,
    UnitType,
    UnitIdentifier,
    ptsCityID,
    State,
    Postal,
  ]);

  const allCandidateFieldsFilled = useMemo(
    () => duplicateCheckFields.every((field) => Boolean(candidateValues[field])),
    [duplicateCheckFields, candidateValues]
  );

  const debouncedCandidateValues = useDebouncedValue(candidateValues, 500);

  const handleCoordinateStateChange = useCallback((state) => setCoordinateState(state), []);

  const handlePrePlanStateChange = useCallback((state) => setPrePlanState(state), []);

  const handleCityCreated = useCallback(
    (city) => {
      methods.setValue('ptsCityID', city?.ptsCityID ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [methods]
  );

  const loadSopsCount = useCallback(() => {
    if (!open || !addressId) return;

    executeApiAction({
      dispatch,
      action: () => getAddressSops(addressId),
      onSuccess: (sopLinks) => setSopsCount((sopLinks || []).length),
    });
  }, [open, addressId, dispatch]);

  const loadAddressDetails = useCallback(() => {
    if (!open || !addressId) return;

    executeApiAction({
      setLoading: setLoadingAddress,
      dispatch,
      action: () => getAddressDetails(addressId),
      onSuccess: (address) => {
        if (!address) return;
        methods.reset({
          ...DEFAULT_VALUES,
          ...address,
          Postal: buildPostal(address.PostalCode, address.PostalCodeExtension),
          Landmark: address.LandmarkText || '',
          CleryLocationRadio: mapCleryFlagsToRadio(address),
        });
      },
    });
  }, [open, addressId, dispatch, methods]);

  const renderTabLabel = useCallback(
    (tab) => {
      if (tab.key === ADDRESS_TAB_KEYS.SOPS && sopsCount > 0) {
        return <span className="rms-tab-count-alert">{`${tab.label} (${sopsCount})`}</span>;
      }

      return tab.label;
    },
    [sopsCount]
  );

  const isActiveSubTabDirty = useCallback(() => {
    if (activeTab === ADDRESS_TAB_KEYS.COORDINATES) {
      return Boolean(coordinatesTabRef.current?.isDirty?.());
    }
    if (activeTab === ADDRESS_TAB_KEYS.PRE_PLAN) {
      return Boolean(prePlanTabRef.current?.isDirty?.());
    }
    return false;
  }, [activeTab]);

  const handleClose = useCallback(() => {
    setDuplicates([]);
    methods.reset(DEFAULT_VALUES);
    onClose?.();
  }, [methods, onClose]);

  const requestTabChange = useCallback(
    (nextTab) => {
      if (nextTab === activeTab) return;
      if (isActiveSubTabDirty()) {
        requestConfirm(
          'You have unsaved changes on this tab. Are you sure you want to leave without saving?',
          () => setActiveTab(nextTab),
          'Unsaved Changes'
        );
        return;
      }
      setActiveTab(nextTab);
    },
    [activeTab, isActiveSubTabDirty, requestConfirm]
  );

  const requestClose = useCallback(() => {
    if (isDirty || isActiveSubTabDirty()) {
      requestConfirm(
        'You have unsaved changes on this address. Are you sure you want to close without saving?',
        () => handleClose(),
        'Unsaved Changes'
      );
      return;
    }

    handleClose();
  }, [isDirty, isActiveSubTabDirty, requestConfirm, handleClose]);

  const onSubmit = useCallback(
    async (data) => {
      const { PostalCode, PostalCodeExtension } = parsePostcode(data.Postal);
      const { CleryLocationRadio, Landmark, ...rest } = data;
      const payload = {
        ...rest,
        PostalCode,
        PostalCodeExtension,
        LandmarkText: Landmark,
        FullAddressText: fullAddressText,
        ...mapCleryLocationFlags(CleryLocationRadio),
      };
      delete payload.Postal;

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () => createAddress({ ...payload, CurrentUser: currentUser?.Username }),
        onSuccess: (address) => {
          dispatch(
            notify(
              isEditMode ? 'Address updated successfully' : 'Address created successfully',
              'success'
            )
          );
          onAddressCreated?.(address);
          handleClose();
        },
      });
    },
    [dispatch, currentUser, fullAddressText, isEditMode, onAddressCreated, handleClose]
  );

  const handleFormSave = methods.handleSubmit(onSubmit);

  const handleSave = useCallback(() => {
    if (activeTab === ADDRESS_TAB_KEYS.COORDINATES) {
      coordinatesTabRef.current?.save?.();
      return;
    }
    if (activeTab === ADDRESS_TAB_KEYS.PRE_PLAN) {
      prePlanTabRef.current?.save?.();
      return;
    }
    handleFormSave();
  }, [activeTab, handleFormSave]);

  useEffect(() => {
    if (!open || isEditMode) return;
    executeApiAction({
      setLoading: setLoadingDuplicateConfig,
      dispatch,
      action: () => getAddressDuplicateCheckFields(),
      onSuccess: (rows) => setDuplicateCheckFields(mapDuplicateCheckFields(rows)),
    });
  }, [open, isEditMode, dispatch]);

  useEffect(() => {
    loadAddressDetails();
  }, [loadAddressDetails]);

  useEffect(() => {
    loadSopsCount();
  }, [loadSopsCount]);

  useEffect(() => {
    if (!open || duplicateCheckFields.length === 0) return;

    if (!allCandidateFieldsFilled) {
      setDuplicates([]);
      return;
    }

    executeApiAction({
      setLoading: setCheckingDuplicates,
      dispatch,
      action: () => findAddressDuplicates(debouncedCandidateValues),
      onSuccess: (result) => setDuplicates(result || []),
    });
  }, [open, duplicateCheckFields, allCandidateFieldsFilled, debouncedCandidateValues, dispatch]);

  const activeSubTabSubmitting =
    activeTab === ADDRESS_TAB_KEYS.COORDINATES
      ? coordinateState.submitting
      : activeTab === ADDRESS_TAB_KEYS.PRE_PLAN
      ? prePlanState.submitting
      : submitting;

  const renderLeftActions = () => {
    if (activeTab === ADDRESS_TAB_KEYS.EDIT) {
      return isEditMode ? (
        <RMSRecordHistoryButton
          disabled={!recordCreated}
          created={recordCreated}
          createdBy={recordCreatedBy}
          updated={recordUpdated}
          updatedBy={recordUpdatedBy}
        />
      ) : null;
    }

    if (activeTab === ADDRESS_TAB_KEYS.COORDINATES) {
      return (
        <RMSRecordHistoryButton
          disabled={!coordinateState.coordinate?.Created}
          created={coordinateState.coordinate?.Created}
          createdBy={coordinateState.coordinate?.CreatedBy}
          updated={coordinateState.coordinate?.Updated}
          updatedBy={coordinateState.coordinate?.UpdatedBy}
        />
      );
    }

    if (activeTab === ADDRESS_TAB_KEYS.PRE_PLAN) {
      return (
        <RMSRecordHistoryButton
          disabled={!prePlanState.prePlan?.Created}
          created={prePlanState.prePlan?.Created}
          createdBy={prePlanState.prePlan?.CreatedBy}
          updated={prePlanState.prePlan?.Updated}
          updatedBy={prePlanState.prePlan?.UpdatedBy}
        />
      );
    }

    return null;
  };

  return (
    <FormProvider {...methods}>
      <RMSCommonDialog
        open={open}
        title={isEditMode ? 'Edit Address' : 'Add Address'}
        onClose={requestClose}
        onSave={handleSave}
        loading={submitting || loadingDuplicateConfig || loadingAddress}
        saveDisabled={activeSubTabSubmitting || !canSaveAddress}
        maxWidth="lg"
        fullWidth
        closeOnBackdropClick={false}
        className="add-address-dialog"
        saveButton={{
          show:
            activeTab === ADDRESS_TAB_KEYS.EDIT ||
            activeTab === ADDRESS_TAB_KEYS.COORDINATES ||
            activeTab === ADDRESS_TAB_KEYS.PRE_PLAN,
          type: 'simple',
          label: 'Save',
          variant: 'contained',
          color: 'primary',
        }}
        cancelButton={{
          show: true,
          type: 'simple',
          label: 'Cancel',
          variant: 'outlined',
          color: 'secondary',
        }}
        leftActions={renderLeftActions()}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => requestTabChange(value)}
          variant="scrollable"
          scrollButtons="auto"
          className="edit-person-dialog__tabs">
          {ADDRESS_TABS.map((tab) => (
            <Tab
              key={tab.key}
              value={tab.key}
              label={renderTabLabel(tab)}
              disabled={tab.key !== ADDRESS_TAB_KEYS.EDIT && !isEditMode}
            />
          ))}
        </Tabs>

        {activeTab === ADDRESS_TAB_KEYS.ATTACHMENTS && (
          <AddressAttachmentsTab ptsAddressID={addressId} />
        )}
        {activeTab === ADDRESS_TAB_KEYS.COORDINATES && (
          <AddressCoordinatesTab
            ref={coordinatesTabRef}
            ptsAddressID={addressId}
            fullAddressText={fullAddressText}
            onStateChange={handleCoordinateStateChange}
          />
        )}
        {activeTab === ADDRESS_TAB_KEYS.HISTORY && <AddressHistoryTab ptsAddressID={addressId} />}
        {activeTab === ADDRESS_TAB_KEYS.PARTIES && <AddressPartiesTab ptsAddressID={addressId} />}
        {activeTab === ADDRESS_TAB_KEYS.PRE_PLAN && (
          <AddressPrePlanTab
            ref={prePlanTabRef}
            ptsAddressID={addressId}
            onStateChange={handlePrePlanStateChange}
          />
        )}
        {activeTab === ADDRESS_TAB_KEYS.SOPS && (
          <AddressSopsTab ptsAddressID={addressId} onSopsChanged={loadSopsCount} />
        )}
        {activeTab === ADDRESS_TAB_KEYS.ZONES && (
          <AddressZonesTab ptsAddressID={addressId} fullAddressText={fullAddressText} />
        )}

        {activeTab === ADDRESS_TAB_KEYS.EDIT && (
          <>
            <Box className="rms-dialog-section rms-address-preview">
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" className="rms-address-preview__text">
                  {fullAddressText}
                </Typography>
                <Box width={220} flexShrink={0} ml={2}>
                  <RMSSelect
                    name="AddressCategory"
                    label="Address Category"
                    options={addressCategoryOptions}
                    {...DICT_SELECT_PROPS}
                  />
                </Box>
              </Box>
            </Box>

            {duplicates.length > 0 && (
              <Box mb={2}>
                <RMSButton
                  label={
                    checkingDuplicates ? 'Checking...' : 'Duplicate Found! Show Duplicate List'
                  }
                  variant="contained"
                  color="secondary"
                  disabled={checkingDuplicates}
                  onClick={() => setDuplicateListOpen(true)}
                />
              </Box>
            )}

            <Box className="rms-dialog-section">
              <SectionLabel>Address Details</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <RMSTextField name="AddressNumberPrefix" label="Prefix" />
                </Grid>
                <Grid item xs={3}>
                  <RMSTextField name="AddressNumber" label="Number" type="number" />
                </Grid>
                <Grid item xs={3}>
                  <RMSTextField name="AddressNumberSuffix" label="Suffix" />
                </Grid>
                <Grid item xs={3}>
                  <RMSSelect
                    name="PreDirection"
                    label="Predirectional"
                    options={predirectionalOptions}
                    {...DICT_SELECT_PROPS}
                  />
                </Grid>
                <Grid item xs={3}>
                  <RMSTextField name="StreetName" label="Street Name" />
                </Grid>
                <Grid item xs={3}>
                  <RMSSelect
                    name="StreetType"
                    label="Type"
                    options={streetTypeOptions}
                    {...DICT_SELECT_PROPS}
                  />
                </Grid>
                <Grid item xs={3}>
                  <RMSSelect
                    name="PostDirection"
                    label="Postdirectional"
                    options={postdirectionalOptions}
                    {...DICT_SELECT_PROPS}
                  />
                </Grid>
                <Grid item xs={3}>
                  <RMSSelect
                    name="ptsCityID"
                    label="City"
                    options={cityDisplayOptions}
                    optionValue="Code"
                    optionLabel="DisplayLabel"
                    showLabelOnly
                    clearable
                    prefixIcon={showCityAddIcon ? <AddIcon fontSize="small" /> : undefined}
                    onPrefixIconClick={
                      showCityAddIcon ? () => setCityManagementDialogOpen(true) : undefined
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <RMSSelect
                    name="State"
                    label="State"
                    options={stateOptions}
                    {...DICT_SELECT_PROPS}
                  />
                </Grid>
                <Grid item xs={3}>
                  <RMSTextField name="Postal" label="Postal Code - Ext." />
                </Grid>
                <Grid item xs={3}>
                  <RMSTextField name="County" label="County" />
                </Grid>
                <Grid item xs={3}>
                  <RMSTextField name="Country" label="Country" />
                </Grid>
              </Grid>
            </Box>

            <Box className="rms-dialog-section">
              <SectionLabel>Notes</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <RMSTextField name="Notes" label="Notes" multiline rows={3} />
                </Grid>
              </Grid>
            </Box>

            <Box className="rms-dialog-section">
              <SectionLabel>Identifiers</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <RMSSelect
                    name="UnitType"
                    label="Unit Type"
                    options={unitTypeOptions}
                    {...DICT_SELECT_PROPS}
                  />
                </Grid>
                <Grid item xs={3}>
                  <RMSTextField name="UnitIdentifier" label="Unit Identifier" />
                </Grid>
                <Grid item xs={3}>
                  <RMSSelect
                    name="FloorType"
                    label="Floor Type"
                    options={floorTypeOptions}
                    {...DICT_SELECT_PROPS}
                  />
                </Grid>
                <Grid item xs={3}>
                  <RMSTextField name="FloorIdentifier" label="Floor Identifier" />
                </Grid>
                <Grid item xs={3}>
                  <RMSSelect
                    name="BldgType"
                    label="Building Type"
                    options={bldgTypeOptions}
                    {...DICT_SELECT_PROPS}
                  />
                </Grid>
                <Grid item xs={3}>
                  <RMSTextField name="BldgIdentifier" label="Building Identifier" />
                </Grid>
              </Grid>
            </Box>

            <Box className="rms-dialog-section">
              <SectionLabel>Flags</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <RMSCheckbox name="ShowInEventLookup" label="Show In Event Lookup" />
                </Grid>
                <Grid item xs={3}>
                  <RMSCheckbox name="SexOffender" label="Sex Offender" />
                </Grid>
                <Grid item xs={3}>
                  <RMSCheckbox name="Parolee" label="Parolee" />
                </Grid>
                <Grid item xs={3}>
                  <RMSCheckbox name="Probation" label="Probation" />
                </Grid>
                <Grid item xs={3}>
                  <RMSCheckbox name="NonValidatedAddress" label="Non Validated Address" />
                </Grid>
              </Grid>
            </Box>

            <Box className="rms-dialog-section">
              <SectionLabel>Clery Location</SectionLabel>
              <RMSRadioGroup name="CleryLocationRadio" options={CLERY_LOCATION_OPTIONS} />
            </Box>

            <Box className="rms-dialog-section">
              <SectionLabel>Location</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <RMSTextField name="CrossStreet" label="Cross Street" />
                </Grid>
                <Grid item xs={4}>
                  <RMSTextField name="Highway" label="Highway" />
                </Grid>
                <Grid item xs={4}>
                  <RMSSelect name="ESN" label="ESN" options={esnOptions} {...ESN_SELECT_PROPS} />
                </Grid>
                <Grid item xs={6}>
                  <RMSTextField name="Landmark" label="Landmark" />
                </Grid>
                <Grid item xs={6}>
                  <RMSTextField name="SurroundingArea" label="Surrounding Area" />
                </Grid>
                <Grid item xs={6}>
                  <RMSTextField name="Area" label="Area" />
                </Grid>
                <Grid item xs={6}>
                  <RMSTextField name="RangeDescription" label="Range Description" />
                </Grid>
                <Grid item xs={12}>
                  <RMSTextField name="Description" label="Description" multiline rows={3} />
                </Grid>
                <Grid item xs={6}>
                  <RMSTextField name="MapLocation" label="Map Location" />
                </Grid>
                <Grid item xs={6}>
                  <RMSTextField name="RelativeLocation" label="Relative Location" />
                </Grid>
              </Grid>
            </Box>

            <Box className="rms-dialog-section rms-dialog-section--last">
              <SectionLabel>Geographical Details</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <RMSTextField name="AltitudeMeasure" label="Altitude Measure" />
                </Grid>
                <Grid item xs={4}>
                  <RMSTextField name="DepthMeasure" label="Depth Measure" />
                </Grid>
                <Grid item xs={4}>
                  <RMSTextField name="GeographicElevation" label="Geographic Elevation" />
                </Grid>
                <Grid item xs={4}>
                  <RMSTextField name="MGRSCoordinate" label="MGRS Coordinate" />
                </Grid>
                <Grid item xs={4}>
                  <RMSTextField name="UTMCoordinate" label="UTM Coordinate" />
                </Grid>
                <Grid item xs={4}>
                  <RMSTextField name="TwoDGeographicCoordinate" label="2-D Geographic Coordinate" />
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </RMSCommonDialog>

      {cityManagementDialogOpen && (
        <CityManagementDialog
          open={cityManagementDialogOpen}
          onClose={() => setCityManagementDialogOpen(false)}
          onCityCreated={handleCityCreated}
        />
      )}

      {duplicateListOpen && (
        <RMSCommonDialog
          open={duplicateListOpen}
          title="Duplicate Address List"
          onClose={() => setDuplicateListOpen(false)}
          maxWidth="sm"
          fullWidth
          saveButton={{ show: false }}
          cancelButton={{
            show: true,
            type: 'simple',
            label: 'Close',
            variant: 'outlined',
            color: 'secondary',
          }}>
          <List>
            {duplicates.map((duplicate) => (
              <ListItem key={duplicate.ptsAddressID}>
                <ListItemText
                  primary={`AddressID: ${duplicate.AddressID || ''}`}
                  secondary={`Full Address: ${duplicate.FullAddressText || ''}`}
                />
              </ListItem>
            ))}
          </List>
        </RMSCommonDialog>
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </FormProvider>
  );
};

export default AddAddressDialog;
